import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SafeImage from '../components/SafeImage';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import {
  resolveAgentImageSrc,
  resolveBuddyImageSrc,
  resolveBundleImageSrc,
  resolveCardImageSrc,
  resolveRankImageSrc,
  resolveSkinImageSrc,
} from '../utils/imageUtils';
import {
  Share2, Trophy, Swords, Boxes, Users, ArrowLeft, Copy, Wallet, Coins, Gem,
  ShoppingBag, Moon, Tag, IdCard, ShieldCheck, ShieldAlert, Sparkles,
  Award, ScrollText, Sticker, Mail, Phone, Check, AlertTriangle, Lock
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, accent = 'orange', right }) => (
  <div className="neon-card rounded-lg p-5">
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded grid place-items-center border ${accent === 'cyan' ? 'border-cyan-400/40 bg-cyan-400/10' : 'border-orange-500/50 bg-orange-500/10'}`}>
          <Icon className={`w-4 h-4 ${accent === 'cyan' ? 'text-cyan-300' : 'text-orange-300'}`} />
        </div>
        <h3 className="font-mono-display text-lg text-white tracking-wide">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

function useCountdown(targetDate) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetDate) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  if (!targetDate) return { d: 0, h: 0, m: 0, s: 0, remaining: 0 };
  const remaining = Math.max(0, new Date(targetDate).getTime() - now);
  const d = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const h = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const m = Math.floor((remaining / (1000 * 60)) % 60);
  const s = Math.floor((remaining / 1000) % 60);
  return { d, h, m, s, remaining };
}

const Countdown = ({ targetDate, withDays = false, accent = 'cyan' }) => {
  const { d, h, m, s } = useCountdown(targetDate);
  const pad = (n) => String(n).padStart(2, '0');
  const color = accent === 'orange' ? 'text-orange-300' : 'text-cyan-300';
  return (
    <div className={`font-mono-display tabular text-lg ${color}`}>
      {withDays && d > 0 ? `${d}d ` : ''}{pad(h)}:{pad(m)}:{pad(s)}
    </div>
  );
};

const CurrencyTile = ({ icon: Icon, label, value, color }) => (
  <div className="rounded border border-zinc-800 bg-black/30 p-3 flex items-center gap-3">
    <div className={`w-10 h-10 rounded grid place-items-center border ${color.border} ${color.bg}`}>
      <Icon className={`w-5 h-5 ${color.text}`} />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className="font-mono-display text-xl text-white tabular">{(value || 0).toLocaleString()}</div>
    </div>
  </div>
);

const FlexBadge = ({ tier, children }) => {
  const colors = {
    gold: 'border-orange-400/60 text-orange-300 bg-orange-500/10',
    platinum: 'border-cyan-400/60 text-cyan-300 bg-cyan-400/10',
    silver: 'border-zinc-500/60 text-zinc-300 bg-zinc-500/10',
    bronze: 'border-amber-700/60 text-amber-400 bg-amber-700/10',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs uppercase tracking-widest ${colors[tier] || colors.silver}`}>
      <Award className="w-3 h-3" />{children}
    </span>
  );
};

const VerificationPill = ({ value, label, icon: Icon }) => {
  const known = typeof value === 'boolean';
  const text = value === true ? `${label} Verified` : value === false ? `${label} Not Verified` : `${label} Status Unknown`;
  const color = value === true ? 'text-cyan-300' : known ? 'text-orange-300' : 'text-zinc-500';
  const StatusIcon = value === true ? ShieldCheck : ShieldAlert;
  return (
    <div className="flex items-center justify-between border-b border-zinc-800 pb-2 gap-3">
      <span className="text-zinc-400 inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{label} Verification</span>
      <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-right ${color}`}>
        <StatusIcon className="w-3.5 h-3.5" />
        {text}
      </span>
    </div>
  );
};

const SkinImage = ({ item, alt, className = 'w-full h-16 object-contain mb-2' }) => {
  const fallbackClass = className.includes('max-') ? 'w-full h-full' : className;
  return (
    <SafeImage
      source={item}
      resolver={resolveSkinImageSrc}
      alt={alt}
      className={className}
      fallbackClassName={fallbackClass}
      fallbackIcon={Boxes}
      fallbackLabel="No image"
    />
  );
};

const rankDisplayName = (rank = {}) => (
  rank.rankName ||
  rank.name ||
  rank.rank ||
  'Unknown Rank'
);

const cleanSeasonPart = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^[0-9a-f]{8}(?:-[0-9a-f]{4})?/i.test(text)) return '';
  return text;
};

const rankEpisodeActLabel = (rank = {}) => {
  const explicit = cleanSeasonPart(rank.episodeActLabel);
  if (explicit) return explicit;

  const episode = cleanSeasonPart(rank.episode);
  const act = cleanSeasonPart(rank.act);
  const combined = [episode, act].filter(Boolean).join(' ');
  if (combined) return combined;

  const seasonName = cleanSeasonPart(rank.seasonName);
  return seasonName || 'Episode/Act not available';
};

const rankDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

export default function AccountDetails({ publicView = false }) {
  const { slug } = useParams();
  const { isAuthed, user } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const url = publicView ? `/accounts/share/${slug}` : `/accounts/${slug}`;
        const { data } = await api.get(url);
        if (!cancelled) setAccount(data.account);
      } catch (e) {
        if (!cancelled) {
          if (!publicView && isAuthed) {
            // Try public fallback if /accounts/{id} fails (e.g. user opens via slug)
            try {
              const { data } = await api.get(`/accounts/share/${slug}`);
              if (!cancelled) setAccount(data.account);
            } catch (e2) {
              setError(e2?.response?.data?.detail || 'Account not found');
            }
          } else {
            setError(e?.response?.data?.detail || 'Account not found');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, publicView, isAuthed]);

  const copyShare = async () => {
    if (!account) return;
    const url = `${window.location.origin}/share/${account.share_slug}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      window.prompt('Copy this share link:', url);
    }
  };

  if (loading) {
    return (
      <Layout>
        <section className="max-w-7xl mx-auto px-6 py-16 text-center">
          <div className="font-mono-display text-zinc-400">Loading account vault...</div>
        </section>
      </Layout>
    );
  }
  if (error || !account) {
    const disabledShare = String(error || '').toLowerCase().includes('disabled');
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16">
          <div className="neon-card rounded-lg p-10 text-center">
            <AlertTriangle className="w-8 h-8 text-orange-300 mx-auto mb-3" />
            <div className="font-mono-display text-2xl text-white mb-2">{disabledShare ? 'Share link disabled' : 'Account not found'}</div>
            <p className="text-sm text-zinc-400 mb-4">{error || 'This share link is invalid or the account was removed.'}</p>
            <Link to={publicView ? '/' : '/dashboard'} className="btn-neon inline-flex"><ArrowLeft className="w-4 h-4" />{publicView ? 'Back to home' : 'Back to dashboard'}</Link>
          </div>
        </section>
      </Layout>
    );
  }

  const inv = account.inventory || {};
  const skins = Array.isArray(inv.skins) ? inv.skins : [];
  const storeSkins = skins.filter(s => s.source === 'store');
  const earnedSkins = skins.filter(s => s.source !== 'store');
  const cur = account.currencies || {};
  const currencies = cur;
  const bpSummary = account.battlepassSummary || { count: 0, spent: 0 };
  const totalUsedVP = storeSkins.reduce((acc, s) => acc + (s.price || 0), 0);
  const totalVP = (cur.vp || 0) + totalUsedVP + (bpSummary.spent || 0);
  const agentVisibility = account.agentVisibility || {};
  const canViewAgents = agentVisibility.can_view_agents ?? ['immortal', 'radiant'].includes((user?.plan || 'free').toLowerCase());
  const ownedAgentsCount = agentVisibility.owned ?? (inv.agents || []).filter(a => a.owned).length;
  const totalAgentsCount = agentVisibility.total ?? (inv.agents || []).length;
  const hasBuddies = (inv.buddies || []).length > 0;
  const hasCards = (inv.cards || []).length > 0;
  const hasSprays = (inv.sprays || []).length > 0;
  const hasTitles = (inv.titles || []).length > 0;
  const hasCosmetics = hasBuddies || hasCards || hasSprays || hasTitles;
  const defaultCosmeticTab = hasBuddies ? 'buddies' : hasCards ? 'cards' : hasSprays ? 'sprays' : 'titles';
  const reg = account.registration || {};
  const regDate = reg.registered_at ? new Date(reg.registered_at) : null;
  const accountAgeYears = regDate ? ((Date.now() - regDate.getTime()) / (1000 * 60 * 60 * 24 * 365)).toFixed(1) : null;
  const dailyShop = account.daily_shop || {};
  const nightMarket = account.night_market || {};
  const bundle = account.bundle || {};
  const matches = account.matches || [];
  const visibleMatches = matches.filter(m => !m.locked);
  const lockedMatches = matches.filter(m => m.locked);
  const matchVisibility = account.matchVisibility || {};
  const matchLimit = matchVisibility.limit || matches.length || 1;
  const matchTitle = matchLimit === 1 ? 'Last Match' : `Last ${matchLimit} Matches`;
  const rankHistory = Array.isArray(account.rank_history) ? account.rank_history : [];
  const totalValueVP = account.totalValueVP || 0;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-8">
        {publicView ? (
          <div className="mb-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-[11px] uppercase tracking-widest">
              <Share2 className="w-3 h-3" /> Public preview
            </div>
            <Link to={isAuthed ? '/dashboard' : '/auth/register'} className="btn-neon">
              {isAuthed ? 'Dashboard' : 'Create your own ValoRex'}
            </Link>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4">
            <Link to="/dashboard" className="btn-ghost"><ArrowLeft className="w-3.5 h-3.5" />Back</Link>
            <button onClick={copyShare} className="btn-neon">
              {copied ? <><Check className="w-4 h-4" />Copied!</> : <><Share2 className="w-4 h-4" />Copy Share Link</>}
            </button>
          </div>
        )}

        <div className="relative rounded-lg overflow-hidden border border-orange-500/20">
          <SafeImage source={account.banner} alt={account.ign} className="w-full h-56 sm:h-72 object-cover" fallbackClassName="w-full h-56 sm:h-72" fallbackIcon={IdCard} fallbackLabel="No banner" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-cyan-300">{account.title}</div>
              <div className="font-mono-display text-3xl sm:text-4xl text-white">{account.ign} <span className="text-orange-300">#{account.tag}</span></div>
              <div className="mt-1 text-sm text-zinc-300">{account.region} • {account.country}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right flex flex-col items-end">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Level</div>
                <div className="font-mono-display neon-orange text-3xl tabular">{account.level}</div>
                <div className="w-24 mt-1">
                   <div className="h-1 w-full bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]" 
                        style={{ width: `${((account.ap_progress || 0) / 5000) * 100}%` }}
                      ></div>
                   </div>
                   <div className="text-[9px] text-zinc-500 mt-0.5 tabular uppercase tracking-tighter">
                      {5000 - (account.ap_progress || 0)} AP TO LVL {account.level + 1}
                   </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Total VP</div>
                <div className="font-mono-display neon-cyan text-3xl tabular">{totalVP.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <Section title="Currencies // Wallet" icon={Wallet} accent="orange">
            <div className="grid sm:grid-cols-3 gap-3">
              <CurrencyTile icon={Coins} label="Unused VP" value={cur.vp} color={{ border: 'border-orange-500/40', bg: 'bg-orange-500/10', text: 'text-orange-300' }} />
              <CurrencyTile icon={Gem} label="Radianite Points" value={cur.radianites} color={{ border: 'border-cyan-400/40', bg: 'bg-cyan-400/10', text: 'text-cyan-300' }} />
              <CurrencyTile icon={Sparkles} label="Kingdom Credits" value={cur.kingdom} color={{ border: 'border-orange-500/40', bg: 'bg-orange-500/10', text: 'text-orange-300' }} />
            </div>
          </Section>
        </div>

        <div className="mt-5">
          <Section title="Account Progress" icon={Sparkles} accent="cyan">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Level Progress */}
              <div className="rounded border border-zinc-800 bg-black/30 p-4">
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Account Level</div>
                    <div className="font-mono-display text-2xl text-white">LVL {account.level}</div>
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tabular">
                    {5000 - (account.ap_progress || 0)} AP to next level
                  </div>
                </div>
                <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.4)]" 
                    style={{ width: `${((account.ap_progress || 0) / 5000) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-[9px] text-zinc-500 uppercase tracking-tighter">
                  <span>{account.ap_progress || 0} AP</span>
                  <span>5000 AP</span>
                </div>
              </div>

              {/* Battlepass Progress */}
              <div className="rounded border border-zinc-800 bg-black/30 p-4">
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Battlepass Season</div>
                    <div className="font-mono-display text-2xl text-white">TIER {account.bp_level || 1}</div>
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tabular text-right">
                    {account.bp_xp_progress?.toLocaleString()} XP Earned
                    <br />
                    <span className="text-cyan-400">Numbers only mode active</span>
                  </div>
                </div>
                <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden opacity-20">
                  <div className="h-full bg-cyan-500 w-full"></div>
                </div>
                <div className="mt-1 text-[9px] text-zinc-500 uppercase tracking-tighter">
                  Progression Status: ACTIVE // CURRENT EPISODE
                </div>
              </div>
            </div>
          </Section>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-5">
          <div className="lg:col-span-1 space-y-5">
            <Section title="Account Info" icon={Users} accent="cyan">
              <div className="space-y-3 text-sm">
                {[
                  ['IGN', `${account.ign} #${account.tag}`],
                  ['Title', account.title],
                  ['Region', account.region],
                  ['Country', account.country],
                  ['Level', String(account.level)],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-zinc-800 pb-2">
                    <span className="text-zinc-400">{k}</span>
                    <span className="text-white font-mono-display">{v}</span>
                  </div>
                ))}
                {!publicView && (
                  <div className="flex items-center gap-2 pt-2">
                    <Copy className="w-3.5 h-3.5 text-cyan-300" />
                    <code className="text-xs text-zinc-400 truncate">{`${window.location.origin}/share/${account.share_slug}`}</code>
                  </div>
                )}
              </div>
            </Section>

            <Section title="Registration & Security" icon={IdCard}>
              <div className="space-y-3 text-sm">
                {regDate && (
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
                    <span className="text-zinc-400">Registered</span>
                    <span className="text-white font-mono-display">{regDate.toLocaleDateString()}</span>
                  </div>
                )}
                {accountAgeYears && (
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
                    <span className="text-zinc-400">Account Age</span>
                    <span className="text-white font-mono-display">{accountAgeYears} yrs</span>
                  </div>
                )}
                {reg.registered_region && (
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
                    <span className="text-zinc-400">Reg. Region</span>
                    <span className="text-white font-mono-display">{reg.registered_region}</span>
                  </div>
                )}
                <VerificationPill value={reg.emailVerified ?? reg.email_verified ?? null} label="Email" icon={Mail} />
                <VerificationPill value={reg.phoneVerified ?? reg.phone_verified ?? null} label="Phone" icon={Phone} />
              </div>
            </Section>

            <Section title="Ranks" icon={Trophy}>
              <div className="flex items-center gap-4 mb-4">
                <SafeImage source={account.rank} resolver={resolveRankImageSrc} alt={account.rank?.name || 'Current rank'} className="w-16 h-16 object-contain" fallbackClassName="w-16 h-16" fallbackIcon={Trophy} />
                <div>
                  <div className="text-[10px] uppercase text-zinc-400">Current Rank</div>
                  <div className="font-mono-display text-2xl text-white">{(account.rank?.name || '').toUpperCase()}</div>
                </div>
              </div>
              {rankHistory.length > 0 && (
                <>
                  <div className="text-[10px] uppercase text-orange-300 tracking-widest mb-2">Previous Ranks</div>
                  <ul className="space-y-2 text-sm">
                    {rankHistory.map((r, i) => (
                      <li key={i} className="flex items-center gap-3 rounded border border-zinc-800 bg-black/30 px-3 py-2">
                        {r.icon ? (
                          <SafeImage source={r} resolver={resolveRankImageSrc} alt={rankDisplayName(r)} className="w-8 h-8 shrink-0 object-contain" fallbackClassName="w-8 h-8 shrink-0" fallbackIcon={Trophy} />
                        ) : (
                          <div className="w-8 h-8 shrink-0 rounded border border-zinc-700 bg-zinc-950 grid place-items-center">
                            <Trophy className="w-4 h-4 text-zinc-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-mono-display text-white truncate">{rankDisplayName(r).toUpperCase()}</div>
                          <div className="text-[11px] uppercase tracking-widest text-zinc-500 truncate">{rankEpisodeActLabel(r)}</div>
                        </div>
                        {(rankDateLabel(r.date) || r.rankRating !== null && r.rankRating !== undefined) && (
                          <div className="shrink-0 text-right text-[10px] uppercase tracking-widest text-zinc-500">
                            {rankDateLabel(r.date) && <div>{rankDateLabel(r.date)}</div>}
                            {r.rankRating !== null && r.rankRating !== undefined && <div className="text-orange-300 tabular">{r.rankRating} RR</div>}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {rankHistory.length === 0 && (
                <div className="rounded border border-dashed border-zinc-700 bg-black/30 py-6 text-center text-sm text-zinc-500 font-mono-display">
                  No previous rank history available
                </div>
              )}
            </Section>

            <Section title={matchTitle} icon={Swords} accent="cyan">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                      <th className="py-2 px-2 text-left">Map</th>
                      <th className="py-2 px-2 text-left">Agent</th>
                      <th className="py-2 px-2">Result</th>
                      <th className="py-2 px-2 text-right">K/D/A</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMatches.map(m => (
                      <tr key={m.id} className="border-t border-zinc-800">
                        <td className="py-2 px-2 text-white">{m.map}</td>
                        <td className="py-2 px-2 text-zinc-300">{m.agent}</td>
                        <td className={`py-2 px-2 text-center font-mono-display text-xs ${m.result === 'Victory' ? 'text-cyan-300' : 'text-red-300'}`}>{m.result}</td>
                        <td className="py-2 px-2 text-right tabular text-zinc-200 text-xs">{m.kda}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {lockedMatches.length > 0 && (
                <div className="mt-3 relative overflow-hidden rounded border border-red-900/70 bg-red-950/20 p-3 text-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-900/25 via-black/20 to-transparent pointer-events-none" />
                  <div className="relative">
                    <Lock className="w-4 h-4 text-red-300 mx-auto mb-2" />
                    <div className="text-xs text-red-200/80 mb-3">Unlock {lockedMatches.length} restricted matches with Immortal or Radiant subscription.</div>
                    <Link to="/pricing" className="btn-neon !py-1 !px-3 text-xs inline-flex mx-auto">
                      Unlock More Matches
                    </Link>
                  </div>
                </div>
              )}
            </Section>
          </div>

          <div className="lg:col-span-2 space-y-5">
            <Section title="Account Analytics" icon={Trophy} accent="orange">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded border border-zinc-800 bg-black/30 p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Total Account Value</div>
               <div className="font-mono-display text-2xl text-white">{totalValueVP.toLocaleString()} <span className="text-orange-300 text-sm">VP</span></div>
              <div className="text-[10px] text-zinc-500 mt-1">
               Store: {(bpSummary.spent > 0 ? totalValueVP - currencies.vp - bpSummary.spent : totalValueVP - currencies.vp).toLocaleString()} VP + BP: {bpSummary.spent.toLocaleString()} VP + Unused: {(currencies.vp || 0).toLocaleString()} VP
           </div>
          </div>
             <div className="rounded border border-zinc-800 bg-black/30 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Battlepass Owned</div>
            <div className="font-mono-display text-2xl text-white">{bpSummary.count} <span className="text-cyan-300 text-sm">Owned</span></div>
           <div className="text-[10px] text-zinc-500 mt-1">Total BP Spent: {bpSummary.spent.toLocaleString()} VP</div>
           </div>
         </div>
        </Section>

            <Tabs defaultValue="inventory" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-black/40 border border-zinc-800 mb-4">
                <TabsTrigger value="inventory" className="data-[state=active]:bg-zinc-800">Inventory</TabsTrigger>
                <TabsTrigger value="battlepass" className="data-[state=active]:bg-zinc-800">Earned Rewards</TabsTrigger>
              </TabsList>
              
              <TabsContent value="inventory">
                <Section
                  title="Inventory // Store Skins"
                  icon={Boxes}
                  accent="cyan"
                  right={
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400">Store Value</div>
                        <div className="font-mono-display tabular text-lg text-orange-300">
                          {storeSkins.reduce((acc, s) => acc + (s.price || 0), 0).toLocaleString()} VP
                        </div>
                      </div>
                    </div>
                  }
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {storeSkins.map(s => (
                      <div key={s.id} className="rounded border border-zinc-800 hover:border-orange-400/60 transition-colors p-3 bg-black/30">
                        <SkinImage item={s} alt={s.name} />
                        <div className="text-xs text-cyan-300 uppercase tracking-widest">{s.weapon}</div>
                        <div className="text-sm text-white font-mono-display truncate">{s.name}</div>
                        <div className="text-xs text-orange-300 tabular">{s.price} VP</div>
                      </div>
                    ))}
                  </div>
                </Section>
              </TabsContent>

              <TabsContent value="battlepass">
                <Section title="Earned // Rewards" icon={Award} accent="orange">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {earnedSkins.map(s => {
                      const sourceLabel = s.source === 'battlepass' ? 'BATTLEPASS' : s.source === 'agent' ? 'AGENT CONTRACT' : 'EVENT REWARD';
                      return (
                        <div key={s.id} className="rounded border border-zinc-800 hover:border-cyan-400/60 transition-colors p-3 bg-black/30">
                          <SkinImage item={s} alt={s.name} />
                          <div className="text-xs text-cyan-300 uppercase tracking-widest">{s.weapon}</div>
                          <div className="text-sm text-white font-mono-display truncate">{s.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{sourceLabel}</div>
                        </div>
                      );
                    })}
                  </div>
                  {earnedSkins.length === 0 && (
                    <div className="py-12 text-center text-zinc-500 font-mono-display">No earned rewards found for this account</div>
                  )}
                </Section>
              </TabsContent>
            </Tabs>

            <Section
              title="Daily Shop"
              icon={ShoppingBag}
              accent="cyan"
              right={dailyShop.refresh_at ? (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">Refresh</span>
                  <Countdown targetDate={dailyShop.refresh_at} accent="cyan" />
                </div>
              ) : null}
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(dailyShop.items || []).map(s => (
                  <div key={s.id} className="rounded border border-zinc-800 hover:border-orange-400/60 transition-colors p-3 bg-black/30">
                    <SkinImage item={s} alt={s.name} />
                    <div className="text-xs text-cyan-300 uppercase tracking-widest">{s.weapon}</div>
                    <div className="text-sm text-white font-mono-display truncate">{s.name}</div>
                    {s.source !== "battlepass" && <div className="text-xs text-orange-300 tabular">{s.price} VP</div>}
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title="Night Market"
              icon={Moon}
              accent="orange"
              right={nightMarket.active && nightMarket.refresh_at ? (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400">Closes in</span>
                  <Countdown targetDate={nightMarket.refresh_at} withDays accent="orange" />
                </div>
              ) : null}
            >
              {nightMarket.active && (nightMarket.items || []).length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {nightMarket.items.map(s => (
                    <div key={s.id} className="relative rounded border border-zinc-800 hover:border-orange-400/60 transition-colors p-3 bg-black/40">
                      <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-500 text-black font-mono-display">-{s.discount}%</span>
                      <SkinImage item={s} alt={s.name} />
                      <div className="text-xs text-cyan-300 uppercase tracking-widest">{s.weapon}</div>
                      <div className="text-sm text-white font-mono-display truncate">{s.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-orange-300 tabular text-sm">{s.price} VP</span>
                        <span className="text-zinc-500 line-through text-xs tabular">{s.original}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded border border-dashed border-zinc-700 bg-black/30 py-12 text-center">
                  <Moon className="w-7 h-7 text-zinc-500 mx-auto mb-2" />
                  <div className="font-mono-display text-zinc-300">Night market is not currently available</div>
                  <div className="text-xs text-zinc-500 mt-1">Check back during the next active window</div>
                </div>
              )}
            </Section>

            {((account.bundles && account.bundles.length > 0) ? account.bundles : [account.bundle]).filter(b => b && b.name).map((b, idx) => (
              <Section
                key={idx}
                title={idx === 0 ? "Current Bundle Offer" : "Additional Bundle"}
                icon={Tag}
                accent="orange"
                right={b.ends_at ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400">Ends in</span>
                    <Countdown targetDate={b.ends_at} withDays accent="orange" />
                  </div>
                ) : null}
              >
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-1">
                    <div className="aspect-square rounded border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-transparent grid place-items-center overflow-hidden">
                      <SafeImage source={b} resolver={resolveBundleImageSrc} alt={b.name} className="w-full h-full object-contain p-3" fallbackClassName="w-full h-full" fallbackIcon={Tag} fallbackLabel="No bundle image" />
                    </div>
                  </div>
                  <div className="sm:col-span-2 flex flex-col justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cyan-300">Featured Bundle</div>
                      <div className="font-mono-display text-2xl text-white mb-2">{b.name}</div>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-4">
                        {(b.items || []).map((it, i) => (
                          <div key={i} className="group/item relative aspect-square rounded border border-zinc-800 bg-black/40 overflow-hidden flex items-center justify-center p-1" title={it?.name}>
                            <SkinImage item={it} alt={it?.name} className="max-w-full max-h-full object-contain" />
                            <div className="absolute inset-0 bg-black/80 opacity-0 group-hover/item:opacity-100 transition-opacity flex flex-col items-center justify-center text-center p-1">
                               <div className="text-[8px] text-orange-300 uppercase tracking-tighter mb-0.5">{it?.type}</div>
                               <div className="text-[9px] text-white font-mono-display leading-tight truncate w-full">{it?.name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400">Bundle Price</div>
                      <div className="font-mono-display text-2xl neon-orange tabular">{(b.price || 0).toLocaleString()} VP</div>
                    </div>
                  </div>
                </div>
              </Section>
            ))}

            {hasCosmetics && (
              <Tabs defaultValue={defaultCosmeticTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 bg-black/40 border border-zinc-800 mb-4">
                  <TabsTrigger value="buddies" className="data-[state=active]:bg-zinc-800" disabled={!hasBuddies}>Buddies</TabsTrigger>
                  <TabsTrigger value="cards" className="data-[state=active]:bg-zinc-800" disabled={!hasCards}>Cards</TabsTrigger>
                  <TabsTrigger value="sprays" className="data-[state=active]:bg-zinc-800" disabled={!hasSprays}>Sprays</TabsTrigger>
                  <TabsTrigger value="titles" className="data-[state=active]:bg-zinc-800" disabled={!hasTitles}>Titles</TabsTrigger>
                </TabsList>

                <TabsContent value="buddies">
                  <Section title="Inventory // Buddies" icon={Sticker} accent="orange">
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {(inv.buddies || []).map(b => (
                        <div key={b.id} className="rounded border border-zinc-800 hover:border-cyan-400/60 transition-colors p-2 bg-black/30">
                          <SafeImage source={b} resolver={resolveBuddyImageSrc} alt={b.name} className="w-full h-14 object-contain" fallbackClassName="w-full h-14" fallbackIcon={Sticker} />
                          <div className="mt-1 text-[10px] text-zinc-300 text-center truncate">{b.name}</div>
                        </div>
                      ))}
                    </div>
                  </Section>
                </TabsContent>

                <TabsContent value="cards">
                  <Section title="Inventory // Player Cards" icon={IdCard} accent="cyan">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {(inv.cards || []).map(c => (
                        <div key={c.id} className="rounded overflow-hidden border border-zinc-800 hover:border-orange-400/60 transition-colors bg-black/30">
                          <SafeImage source={c} resolver={resolveCardImageSrc} alt={c.name} className="w-full h-24 object-cover" fallbackClassName="w-full h-24" fallbackIcon={IdCard} />
                          <div className="p-2 text-xs text-zinc-300 text-center truncate">{c.name}</div>
                        </div>
                      ))}
                    </div>
                  </Section>
                </TabsContent>

                <TabsContent value="sprays">
                  <Section title="Inventory // Sprays" icon={Sparkles}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(inv.sprays || []).map(sp => (
                        <div key={sp.id} className="rounded border border-zinc-800 px-3 py-2 bg-black/30 text-sm text-white flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />{sp.name}
                        </div>
                      ))}
                    </div>
                  </Section>
                </TabsContent>

                <TabsContent value="titles">
                  <Section title="Inventory // Titles" icon={ScrollText} accent="cyan">
                    <div className="flex flex-wrap gap-2">
                      {(inv.titles || []).map(t => (
                        <span key={t.id} className={`px-3 py-1.5 rounded border text-sm ${t.equipped ? 'border-orange-400/70 text-orange-200 bg-orange-500/10' : 'border-zinc-700 text-zinc-300'}`}>
                          {t.text}{t.equipped && <span className="ml-2 text-[10px] uppercase tracking-widest text-orange-300">Equipped</span>}
                        </span>
                      ))}
                    </div>
                  </Section>
                </TabsContent>
              </Tabs>
            )}

            {totalAgentsCount > 0 && (
              <Section title="Agents" icon={Users} accent={canViewAgents ? 'cyan' : 'orange'}>
                {canViewAgents ? (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                    {(inv.agents || []).map(a => (
                      <div key={a.id} className={`relative rounded p-2 border transition-colors ${a.owned ? 'border-orange-400/40 bg-orange-500/5' : 'border-zinc-800 opacity-50'}`}>
                        <SafeImage source={a} resolver={resolveAgentImageSrc} alt={a.name} className="w-full h-16 object-contain" fallbackClassName="w-full h-16" fallbackIcon={Users} />
                        <div className="mt-1 text-center">
                          <div className="text-xs text-white">{a.name}</div>
                          <div className="text-[10px] uppercase text-zinc-500">{a.role}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative overflow-hidden rounded border border-red-900/70 bg-red-950/20 p-5">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-900/25 via-black/20 to-transparent pointer-events-none" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-red-300 mb-2">
                          <Lock className="w-3.5 h-3.5" /> Immortal+ Feature
                        </div>
                        <div className="font-mono-display text-2xl text-white">
                          {ownedAgentsCount}/{totalAgentsCount} <span className="text-red-300 text-sm">Agents Unlocked</span>
                        </div>
                        <div className="text-xs text-red-200/60 mt-1">
                          {publicView
                            ? "Agent details are hidden by this account owner's plan."
                            : 'Upgrade to Immortal or Radiant to view the full unlocked agent list.'}
                        </div>
                      </div>
                      {!publicView && (
                        <Link to="/pricing" className="btn-neon inline-flex justify-center">
                          Unlock
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {(inv.flexes || []).length > 0 && (
              <Section title="Flexes // Achievements" icon={Award} accent="orange">
                <div className="flex flex-wrap gap-2">
                  {inv.flexes.map(f => (
                    <FlexBadge key={f.id} tier={f.tier}>{f.text}</FlexBadge>
                  ))}
                </div>
              </Section>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
